import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { trackPurchase } from "@/lib/fbPixel";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export function PaymentSuccessPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ phase: "polling", payload: null, error: "" });
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error", payload: null, error: "missing session_id" }); return; }
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", payload: data, error: "" });
          // Guard against double counting: StrictMode double-mount or a
          // reload of this confirmation page for an already-paid session.
          const trackedKey = `fbq_purchase_${sessionId}`;
          if (!localStorage.getItem(trackedKey)) {
            // the listed price in USD when known (same unit for every purchase), else what was charged
            if (data.original_amount) trackPurchase(Number(data.original_amount), data.original_currency || "USD", sessionId, data.kind);
            else trackPurchase(data.amount_total ? data.amount_total / 100 : undefined, data.currency, sessionId, data.kind);
            localStorage.setItem(trackedKey, "1");
          }
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", payload: data, error: "" });
          return;
        }
        if (attempts >= 8) {
          setState({ phase: "timeout", payload: data, error: "" });
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setState({ phase: "error", payload: null, error: e.response?.data?.detail || "Erreur" });
      }
    };
    tick();
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] text-white px-4">
      <div className="max-w-md w-full rounded-3xl p-8 bg-white/[0.03] border border-white/10 text-center" data-testid="payment-result">
        {state.phase === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-[#FFD60A] animate-spin mx-auto" />
            <h1 className="mt-4 text-2xl font-black font-display">Vérification du paiement…</h1>
            <p className="text-sm text-zinc-400 mt-2">Quelques secondes…</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h1 className="mt-4 text-2xl font-black font-display">Paiement réussi !</h1>
            <p className="text-sm text-zinc-400 mt-2">
              {state.payload.amount_total ? `${(state.payload.amount_total / 100).toFixed(2)} ${(state.payload.currency || "").toUpperCase()}` : "Confirmé"}
            </p>
            <button onClick={() => navigate("/library")} className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2" data-testid="goto-library">
              Voir ma bibliothèque <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {(state.phase === "expired" || state.phase === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-amber-400 mx-auto" />
            <h1 className="mt-4 text-2xl font-black font-display">Session expirée ou en attente</h1>
            <p className="text-sm text-zinc-400 mt-2">Réessayez ou contactez le support.</p>
            <Link to="/store" className="btn-secondary mt-6 w-full inline-flex items-center justify-center" data-testid="back-to-store">Retour à la boutique</Link>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto" />
            <h1 className="mt-4 text-2xl font-black font-display">Erreur</h1>
            <p className="text-sm text-zinc-400 mt-2">{state.error}</p>
            <Link to="/store" className="btn-secondary mt-6 w-full inline-flex items-center justify-center">Retour à la boutique</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] text-white px-4">
      <div className="max-w-md w-full rounded-3xl p-8 bg-white/[0.03] border border-white/10 text-center" data-testid="payment-cancel">
        <XCircle className="w-14 h-14 text-amber-400 mx-auto" />
        <h1 className="mt-4 text-2xl font-black font-display">Paiement annulé</h1>
        <p className="text-sm text-zinc-400 mt-2">Aucun débit n'a été effectué.</p>
        <Link to="/store" className="btn-secondary mt-6 w-full inline-flex items-center justify-center">Retour à la boutique</Link>
      </div>
    </div>
  );
}
